import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authApi } from '@/features/auth/api/auth.api';
import { pickPostLoginRoute } from '@/features/auth/utils/handlePostAuth';
import { TravelerProfileSetupForm } from './TravelerProfileSetupForm';
import { BrandLogo } from '@/shared/components/BrandLogo';
import type { MeResponse } from '@/types';

/**
 * Blocks authenticated travelers with an incomplete profile until they submit
 * first name, last name, and email. Closing dismisses the session (logout) so
 * incomplete form details are never treated as a completed profile; the dialog
 * returns on the next login until they finish.
 */
export function TravelerProfileGate() {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isInitializing,
    requiresProfileSetup,
    isAdmin,
    isApprovedGuide,
    isGuideApplicant,
    user,
    postAuthRoute,
    setMe,
    logout,
  } = useAuth();

  const mustComplete =
    isAuthenticated &&
    !isInitializing &&
    requiresProfileSetup &&
    !isAdmin &&
    !isApprovedGuide &&
    !isGuideApplicant;

  const handleDismiss = async () => {
    try {
      await authApi.logout();
    } catch {
      // clear local session even if the API call fails
    }
    logout();
    navigate('/', { replace: true });
  };

  const handleSuccess = (me: MeResponse) => {
    const pendingRoute = postAuthRoute;
    setMe({
      ...me,
      postAuthRoute:
        pendingRoute && pendingRoute !== '/' ? pendingRoute : me.postAuthRoute,
    });
    const route =
      pendingRoute && pendingRoute !== '/'
        ? pendingRoute
        : pickPostLoginRoute(me);
    navigate(route, { replace: true });
  };

  if (!mustComplete) return null;

  return (
    <Dialog
      open
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          void handleDismiss();
        }
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 'var(--radius-xl)',
          bgcolor: 'background.paper',
          boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 2,
          pt: 2,
          pb: 0,
        }}
      >
        <IconButton onClick={() => void handleDismiss()} size="small" aria-label="close and sign out">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 3, sm: 4 }, pt: 1, pb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <BrandLogo size="md" surface="light" />
        </Box>
        <TravelerProfileSetupForm
          compact
          defaultValues={{
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
          }}
          onSuccess={handleSuccess}
        />
      </DialogContent>
    </Dialog>
  );
}
